import React, { useEffect, useMemo, useState } from "react";
import "./Landing.css";
import LogoLight from "../HiveDebitW.png";
import LogoDark from "../HiveDebitB.png";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import rehypeSanitize from "rehype-sanitize";
import { defaultSchema } from "hast-util-sanitize";

const HIVE_RPC = "https://api.hive.blog";

async function hive_get_content(author, permlink) {
  const r = await fetch(HIVE_RPC, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ jsonrpc: "2.0", method: "condenser_api.get_content", params: [author.replace(/^@/, ""), permlink], id: 1 }) });
  const j = await r.json();
  return j.result;
}
function parsePeakD(url) {
  try {
    const u = new URL(url);
    const p = u.pathname.split("/").filter(Boolean);
    const a = (p[1] || "").replace("@", "");
    const per = p[2] || "";
    if (!a || !per) throw 0;
    return { author: a, permlink: per };
  } catch { return null; }
}
function excerpt(md, max = 160) {
  const text = md.replace(/<\/?center\s*\/?>/gi, " ").replace(/<[^>]+>/g, " ").replace(/!\[[^\]]*\]\([^)]+\)/g, "").replace(/\[[^\]]*\]\([^)]+\)/g, "").replace(/[`*_>#-]/g, " ").replace(/\s+/g, " ").trim();
  return text.length > max ? text.slice(0, max) + "…" : text;
}

const EN = {
  go_app:"Go to HiveDebit", hero_title:"Payments that feel effortless", hero_sub:"Create invoices on Hive/HBD/Lightning via QR or NFC. Manage your card and read tutorials in-app.",
  enter:"Enter HiveDebit", see_features:"See features", overview_title:"At a glance",
  f1:"Invoices that just work", f1d:"Create charges in Hive/HBD/Lightning with QR and NFC flows ready for real stores.",
  f2:"Web NFC", f2d:"Create and read cards from Android/Chrome. iPhone shows availability status so users know what’s supported.",
  f3:"Hive integrated", f3d:"Keychain for votes, HBD tips and comments inside our own UI.",
  wn_title:"What’s new — Grand Cherokee", wn_1_t:"Faster transactions", wn_1_d:"Lower-latency requests, batched operations and pre-validation. Less waiting, more confirmations.",
  wn_2_t:"Security-first account flow", wn_2_d:"New onboarding requires creating a fresh account with safer defaults and clearer recovery guidance.",
  wn_3_t:"Stability & polish", wn_3_d:"Smoother modals, consistent buttons, improved NFC states and theme/i18n transitions without layout shift.",
  fx_title:"Capabilities", fx_sub:"Deep integrations you can rely on.",
  fx_imessage:"Send Hive in Messages", fx_imessage_d:"Share a secure request link in Messages so the other side confirms and funds instantly.", fx_imessage_note:"If native handoff isn’t available, a web confirmation opens with the same safeguards.",
  fx_airdrop:"AirDrop handoffs", fx_airdrop_d:"Pass invoice payloads or signed requests to nearby Apple devices. Great when connectivity is weak.", fx_airdrop_note:"Only metadata is shared. Devices verify signatures on open.",
  fx_lightning:"Lightning from cards", fx_lightning_d:"Accept payments from NFC Lightning cards and compatible wallets. Amount, memo and expiry are encoded.", fx_lightning_note:"If Lightning isn’t reachable, the flow falls back to QR seamlessly.",
  fx_vouchers:"Offline Vouchers", fx_vouchers_d:"Issue prepaid vouchers redeemable without internet. Settlement happens when the device reconnects.", fx_vouchers_note:"Signed payloads and one-time nonces prevent reuse and forgery.",
  posts_title:"Latest tutorials", posts_sub:"Opened in our own UI. View originals on PeakD.",
  open:"Open", view_on_peakd:"View on PeakD", footer_powered:"Powered by Hive",
  theme:"Theme", language:"Language", light:"Light", dark:"Dark",
  founder_role_1:"Founder & Developer", founder_role_2:"Co-Founder • Project Manager • Investor", founder_role_3:"Co-Founder • Investor",
  version_label:"VERSION", story_title:"What is HiveDebit?", story_lead:"A practical toolkit for accepting and moving value on Hive that feels native on the web.",
  story_p1:"Create invoices in Hive or HBD, receive from Lightning, hand off by NFC or QR, and let customers interact via Hive Keychain without leaving your flow.",
  story_p2:"For merchants and creators who care about speed, self-custody and clarity. Offline vouchers cover low-connectivity cases.",
  story_p3:"Grand Cherokee focuses on performance, safer defaults and a cleaner UI so you can trust it in real environments."
};

function useI18n(){
  const RAW_A = import.meta.glob("../dictionary/*.json", { eager: true });
  const RAW_B = import.meta.glob("../../dictionary/*.json", { eager: true });
  const RAW = { ...RAW_A, ...RAW_B };
  const [dicts,setDicts]=useState([]);
  const initial = useMemo(()=>{ const saved=localStorage.getItem("lang"); const nav=(navigator.language||"en").slice(0,2).toLowerCase(); return saved||nav; },[]);
  const [lang,setLang]=useState(initial);
  const [strings,setStrings]=useState(EN);
  useEffect(()=>{
    const list = Object.entries(RAW).map(([p,m])=>{
      const raw=m?.default??m??{};
      const code=(raw.code || p.match(/([a-z-]+)\.json$/i)?.[1] || "xx").toLowerCase();
      const language=raw.language || raw.name || code.toUpperCase();
      const src=raw.strings?raw.strings:raw;
      const s=Object.fromEntries(Object.entries(src).filter(([k])=>!["code","language","name","strings"].includes(k)));
      return {code,language,strings:s};
    });
    const map=new Map();
    map.set("en",{code:"en",language:"English",strings:EN});
    list.forEach(d=>map.set(d.code,d));
    setDicts(Array.from(map.values()));
  },[]);
  useEffect(()=>{
    localStorage.setItem("lang",lang);
    const found=dicts.find(d=>d.code===lang) || dicts.find(d=>d.code==="en");
    setStrings(found?.strings||EN);
    document.body.classList.add("lang-swap");
    const t=setTimeout(()=>document.body.classList.remove("lang-swap"),220);
    return ()=>clearTimeout(t);
  },[lang,dicts]);
  const t=(k)=>strings?.[k]??EN[k]??k;
  return {lang,setLang,t,dicts};
}

function useTheme(){
  const [theme,setThemeState]=useState(()=>localStorage.getItem("theme")||"dark");
  useEffect(()=>{
    document.documentElement.classList.toggle("dark", theme==="dark");
    let meta=document.querySelector('meta[name="theme-color"]'); if(!meta){meta=document.createElement("meta"); meta.setAttribute("name","theme-color"); document.head.appendChild(meta);}
    meta.setAttribute("content", theme==="dark"?"#0E0E12":"#F4F5F7");
    localStorage.setItem("theme",theme);
  },[theme]);
  const setTheme=(next)=>{ document.body.classList.add("theme-swap"); setThemeState(next); setTimeout(()=>document.body.classList.remove("theme-swap"),220); };
  return {theme,setTheme};
}

function useParallax(){
  useEffect(()=>{
    const reduce=window.matchMedia?.("(prefers-reduced-motion: reduce)").matches; if(reduce) return;
    let raf=0;
    const onScroll=()=>{ cancelAnimationFrame(raf); raf=requestAnimationFrame(()=>{ const y=window.scrollY||0; const h=window.innerHeight||1; const r=Math.max(0,Math.min(1,(y%(h*1.4))/(h*1.4))); const scale=0.94+Math.sin(r*Math.PI)*0.16; const ty=(Math.cos(r*Math.PI)*6).toFixed(1); document.documentElement.style.setProperty("--heroScale",String(scale)); document.documentElement.style.setProperty("--heroY",ty+"px"); document.documentElement.style.setProperty("--plx1",(y*0.06).toFixed(1)+"px"); document.documentElement.style.setProperty("--plx2",(y*0.12).toFixed(1)+"px"); }); };
    onScroll(); window.addEventListener("scroll",onScroll,{passive:true}); return ()=>{ cancelAnimationFrame(raf); window.removeEventListener("scroll",onScroll); };
  },[]);
}

function useIsMobile(bp=820){
  const [isMobile,setIsMobile]=useState(()=> window.matchMedia && window.matchMedia(`(max-width:${bp}px)`).matches);
  useEffect(()=>{
    const mq=window.matchMedia(`(max-width:${bp}px)`); const on=e=>setIsMobile(e.matches);
    mq.addEventListener ? mq.addEventListener("change",on) : mq.addListener(on);
    return ()=>{ mq.removeEventListener ? mq.removeEventListener("change",on) : mq.removeListener(on); };
  },[bp]);
  return isMobile;
}
function usePlatform(){
  const ua=(typeof navigator!=="undefined"?navigator.userAgent:"").toLowerCase();
  const isAndroid=/android/.test(ua);
  const isIOS=/iphone|ipad|ipod/.test(ua);
  return {isAndroid,isIOS};
}

function LangMenu({ value, onChange, options, label, compact }){
  const [open,setOpen]=useState(false);
  const current = options.find(o=>o.code===value) || options[0];
  useEffect(()=>{ const f=e=>{ if(!e.target.closest(".lang-menu")) setOpen(false); }; window.addEventListener("click",f); return ()=>window.removeEventListener("click",f); },[]);
  return (
    <div className={`lang-menu ${open?"open":""} ${compact?"compact":""}`}>
      <button className="chip" aria-label={label} onClick={()=>setOpen(o=>!o)}>
        <span className="chip-dot" />
        <span className="chip-text">{current?.language||"Language"}</span>
        <span className="chip-caret">▾</span>
      </button>
      <div className="lang-pop">
        {options.map(o=>(
          <button key={o.code} className={`lang-item ${o.code===value?"active":""}`} onClick={()=>{onChange(o.code); setOpen(false);}}>
            {o.language}
          </button>
        ))}
      </div>
    </div>
  );
}

function ThemeToggle({ theme, setTheme, t, label }){
  const next = theme==="dark"?"light":"dark";
  return (
    <button className="btn mode-btn" onClick={()=>setTheme(next)} aria-label={t("theme")}>
      <span className="mode-ico">{theme==="dark"?"☀":"☾"}</span>
      {label ? <span className="mode-label">{theme==="dark"?t("light"):t("dark")}</span> : null}
    </button>
  );
}

function Ico({ type }){
  if(type==="invoice") return (<svg width="28" height="28" viewBox="0 0 24 24"><path d="M7 3h8l4 4v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z" fill="currentColor" opacity=".18"/><path d="M8 9h7M8 12h8M8 15h6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" fill="none"/></svg>);
  if(type==="nfc") return (<svg width="28" height="28" viewBox="0 0 24 24"><rect x="3" y="6" width="18" height="12" rx="2" fill="currentColor" opacity=".18"/><path d="M8 9h8M6.5 13.5h5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>);
  if(type==="hive") return (<svg width="28" height="28" viewBox="0 0 24 24"><path d="M5 12l5-7h4l-5 7 5 7H10z" fill="currentColor" opacity=".18"/><path d="M5 12l5-7h4l-5 7 5 7H10z" stroke="currentColor" strokeWidth="1.8" fill="none"/></svg>);
  if(type==="msg") return (<svg width="28" height="28" viewBox="0 0 24 24"><path d="M4 5h16v10H7l-3 4z" fill="currentColor" opacity=".18"/><path d="M4 5h16v10H7l-3 4z" stroke="currentColor" strokeWidth="1.8" fill="none"/></svg>);
  if(type==="air") return (<svg width="28" height="28" viewBox="0 0 24 24"><circle cx="12" cy="12" r="7" fill="currentColor" opacity=".18"/><path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>);
  if(type==="ln") return (<svg width="28" height="28" viewBox="0 0 24 24"><path d="M6 13l7-10-3 8h8l-7 10 3-8z" fill="currentColor" opacity=".18"/><path d="M6 13l7-10-3 8h8l-7 10 3-8z" stroke="currentColor" strokeWidth="1.8" fill="none"/></svg>);
  if(type==="voucher") return (<svg width="28" height="28" viewBox="0 0 24 24"><rect x="4" y="6" width="16" height="12" rx="2" fill="currentColor" opacity=".18"/><path d="M8 9h8M8 12h6M8 15h4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>);
  return null;
}

function JeepCluster({ t }){
  const VERSION="Grand Cherokee v0.1";
  const TEAM=[
    { user:"jthomasewsky", name:"Jan Thomasewsky", role:t("founder_role_1"), avatar:"https://images.hive.blog/u/jthomasewsky/avatar" },
    { user:"starkerz", name:"Starkerz", role:t("founder_role_2"), avatar:"https://images.hive.blog/u/starkerz/avatar" },
    { user:"theycallmedan", name:"TheyCallMeDan", role:t("founder_role_3"), avatar:"https://images.hive.blog/u/theycallmedan/avatar" },
  ];
  const [hive,setHive]=useState(null); const [btc,setBtc]=useState(null);
  const [hChg,setHChg]=useState(null); const [bChg,setBChg]=useState(null);
  const [idx,setIdx]=useState(0); const [glow,setGlow]=useState(false);
  useEffect(()=>{ let alive=true; async function load(){ try{ const r=await fetch("https://api.coingecko.com/api/v3/simple/price?ids=hive,bitcoin&vs_currencies=usd&include_24hr_change=true"); const j=await r.json(); if(!alive) return; setHive(j?.hive?.usd??null); setBtc(j?.bitcoin?.usd??null); setHChg(j?.hive?.usd_24h_change??null); setBChg(j?.bitcoin?.usd_24h_change??null);}catch{} } load(); const id=setInterval(load,30000); return ()=>{alive=false; clearInterval(id);}; },[]);
  useEffect(()=>{ const tmr=setInterval(()=>{ setIdx(i=>(i+1)%TEAM.length); setGlow(g=>!g); },10000); return ()=>clearInterval(tmr); },[]);
  const p=TEAM[idx];
  const fU=n=> n==null?"—":(n>=1000?n.toLocaleString(undefined,{maximumFractionDigits:0}):n.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2}));
  const fP=n=> n==null?"—":`${Math.abs(n).toFixed(2)}%`; const ar=n=> n==null?"":(n>=0?"▲":"▼");
  return (
    <section className="lx-section">
      <div className="lx-container">
        <div className="cluster">
          <div className="cluster-slot">
            <div className="cluster-frame">
              <div className="cluster-kv"><span className="cluster-label">HIVE</span><span className="cluster-value">${fU(hive)}</span></div>
              <div className={`cluster-trend ${hChg>=0?"up":"down"}`}>{ar(hChg)} {fP(hChg)}</div>
              <div className="cluster-kv" style={{marginTop:6}}><span className="cluster-label">BTC</span><span className="cluster-value">${fU(btc)}</span></div>
              <div className={`cluster-trend ${bChg>=0?"up":"down"}`}>{ar(bChg)} {fP(bChg)}</div>
              <div className="cluster-kv version" style={{marginTop:8}}><span className="cluster-label">{t("version_label")}</span><span className="cluster-value">{VERSION}</span></div>
            </div>
          </div>
          <div className="cluster-slot">
            <div className="cluster-frame">
              <div key={p.user} className={`cluster-profile ${glow?"glow":""}`}>
                <img className="cluster-avatar" src={p.avatar} alt={p.user} width="96" height="96" loading="lazy" />
                <div className="cluster-name">{p.name}</div>
                <div className="cluster-role">{p.role}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function PostModal({ open, onClose, title, body }){
  const schema = useMemo(()=>{ const s=structuredClone(defaultSchema); s.tagNames=[...(s.tagNames||[]),"center"]; s.attributes={...(s.attributes||{}),"*":[...(s.attributes?.["*"]||[]),"className","align"],a:[...(s.attributes?.a||[]),"href","name","target","rel"],img:[...(s.attributes?.img||[]),"src","alt","title","width","height"]}; return s;},[]);
  const [closing,setClosing]=useState(false);
  if(!open) return null;
  const close=()=>{ setClosing(true); setTimeout(()=>{ setClosing(false); onClose(); },160); };
  return (
    <div className={`lx-modal-overlay ${closing?"closing":""}`} onClick={(e)=>{ if(e.currentTarget===e.target) close(); }}>
      <div className="lx-modal-sheet" onClick={(e)=>e.stopPropagation()}>
        <div className="lx-modal-head">
          <strong className="lx-modal-title">{title}</strong>
          <button className="btn" onClick={close}>Close</button>
        </div>
        <div className="lx-modal-body">
          <div className="prose">
            <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw,[rehypeSanitize,schema]]} linkTarget="_blank" components={{a:(p)=><a {...p} rel="noreferrer noopener"/>, center:({children})=><div style={{textAlign:"center"}}>{children}</div>}}>
              {body||""}
            </ReactMarkdown>
          </div>
        </div>
      </div>
    </div>
  );
}

function PostsSection({ t }){
  const refs=useMemo(()=>[
    "https://peakd.com/hive-181335/@hivedebit/rxmojmynvj",
    "https://peakd.com/hive-106130/@hivedebit/emioepnytu",
    "https://peakd.com/hive-106130/@hivedebit/xqudahfpnq",
  ].map(parsePeakD).filter(Boolean),[]);
  const [items,setItems]=useState([]);
  const [viewer,setViewer]=useState({open:false,title:"",body:""});
  useEffect(()=>{ let alive=true;
    Promise.all(refs.map(async r=>{ const data=await hive_get_content(r.author,r.permlink); let cover=null; try{ const meta=data?.json_metadata?JSON.parse(data.json_metadata):null; if(meta?.image?.length) cover=meta.image[0]; }catch{} return { key:r.permlink, author:r.author, permlink:r.permlink, title:data?.title||`${r.author}/${r.permlink}`, cover, link:`https://peakd.com/@${r.author}/${r.permlink}`, body:data?.body||"" }; }))
      .then(list=>{ if(alive) setItems(list); });
    return ()=>{alive=false};
  },[refs]);
  return (
    <section className="lx-section">
      <div className="lx-container">
        <div className="lx-cxh">
          <h2 className="lx-h2">{t("posts_title")}</h2>
          <p className="lx-copy">{t("posts_sub")}</p>
        </div>
        <div className="lx-grid-posts equalize">
          {items.map(it=>(
            <article key={it.key} className="lx-post">
              {it.cover ? <img className="lx-post-img" src={it.cover} alt="" /> : <div className="lx-post-ph" />}
              <div className="lx-post-main">
                <h4 className="lx-post-title">{it.title}</h4>
                <p className="lx-post-excerpt">{excerpt(it.body,160)}</p>
                <div className="lx-post-actions">
                  <button className="btn btn-primary" onClick={()=>setViewer({open:true,title:it.title,body:it.body})}>{t("open")}</button>
                  <a className="btn" href={it.link} target="_blank" rel="noreferrer">{t("view_on_peakd")}</a>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
      <PostModal open={viewer.open} onClose={()=>setViewer(v=>({...v,open:false}))} title={viewer.title} body={viewer.body}/>
    </section>
  );
}

function DesktopCapabilities({ t }){
  const rows=[
    { icon:"msg", k:t("fx_imessage"), v:t("fx_imessage_d"), note:t("fx_imessage_note") },
    { icon:"air", k:t("fx_airdrop"), v:t("fx_airdrop_d"), note:t("fx_airdrop_note") },
    { icon:"ln", k:t("fx_lightning"), v:t("fx_lightning_d"), note:t("fx_lightning_note") },
    { icon:"voucher", k:t("fx_vouchers"), v:t("fx_vouchers_d"), note:t("fx_vouchers_note") },
  ];
  return (
    <section id="features" className="lx-section caps caps-desktop">
      <div className="lx-container">
        <div className="lx-cxh">
          <h2 className="lx-h2">{t("fx_title")}</h2>
          <p className="lx-copy">{t("fx_sub")}</p>
        </div>
      </div>
      <div className="caps-stack">
        {rows.map((r,i)=>(
          <section key={r.k} className={`cap ${i%2===0?"left":"right"}`} data-cap>
            <div className="lx-container">
              <div className="cap-inner">
                <div className="cap-aside">
                  <div className="cap-num">{String(i+1).padStart(2,"0")}</div>
                  <div className="cap-bar" />
                </div>
                <div className="cap-ico"><Ico type={r.icon}/></div>
                <div className="cap-copy">
                  <h3 className="cap-title">{r.k}</h3>
                  <p className="cap-text">{r.v}</p>
                  <div className="cap-note"><span className="cap-note-badge">Note</span><span className="cap-note-text">{r.note}</span></div>
                </div>
              </div>
            </div>
          </section>
        ))}
      </div>
    </section>
  );
}

function MobileCapabilitiesStack({ t }){
  const rows=[
    { icon:"msg", k:t("fx_imessage"), v:t("fx_imessage_d"), note:t("fx_imessage_note") },
    { icon:"air", k:t("fx_airdrop"), v:t("fx_airdrop_d"), note:t("fx_airdrop_note") },
    { icon:"ln", k:t("fx_lightning"), v:t("fx_lightning_d"), note:t("fx_lightning_note") },
    { icon:"voucher", k:t("fx_vouchers"), v:t("fx_vouchers_d"), note:t("fx_vouchers_note") },
  ];
  useEffect(()=>{
    const wrap=document.querySelector(".caps-stack-android");
    if(!wrap) return;
    const cards=Array.from(wrap.querySelectorAll(".cap-stack-slide"));
    const onScroll=()=>{
      const idx=wrap.scrollLeft / wrap.clientWidth;
      cards.forEach((c,i)=>{
        const dist=Math.abs(i - idx);
        const scale=1 - Math.min(dist,1)*0.08;
        const y=Math.min(dist,1)*8;
        c.firstElementChild.style.transform=`translateY(${y}px) scale(${scale})`;
        c.firstElementChild.style.opacity= String(1 - Math.min(dist,1)*0.18);
      });
    };
    onScroll();
    wrap.addEventListener("scroll",onScroll,{passive:true});
    const advance=()=>wrap.scrollTo({left: ((Math.round(wrap.scrollLeft / wrap.clientWidth)+1)%cards.length)*wrap.clientWidth, behavior:"smooth"});
    const id=setInterval(advance, 3800);
    return ()=>{ wrap.removeEventListener("scroll",onScroll); clearInterval(id); };
  },[]);
  return (
    <section id="features" className="lx-section caps caps-android">
      <div className="lx-container">
        <div className="lx-cxh">
          <h2 className="lx-h2">{t("fx_title")}</h2>
          <p className="lx-copy">{t("fx_sub")}</p>
        </div>
      </div>
      <div className="caps-stack-android" data-snap>
        {rows.map((r,i)=>(
          <div key={r.k} className="cap-stack-slide">
            <article className="cap-stack-card">
              <div className="cap-ico big"><Ico type={r.icon}/></div>
              <div className="cap-num top">{String(i+1).padStart(2,"0")}</div>
              <h3 className="cap-title">{r.k}</h3>
              <p className="cap-text">{r.v}</p>
              <div className="cap-note"><span className="cap-note-badge">Note</span><span className="cap-note-text">{r.note}</span></div>
            </article>
          </div>
        ))}
      </div>
    </section>
  );
}

function MobileCapabilitiesDefault({ t }){
  const rows=[
    { icon:"msg", k:t("fx_imessage"), v:t("fx_imessage_d"), note:t("fx_imessage_note") },
    { icon:"air", k:t("fx_airdrop"), v:t("fx_airdrop_d"), note:t("fx_airdrop_note") },
    { icon:"ln", k:t("fx_lightning"), v:t("fx_lightning_d"), note:t("fx_lightning_note") },
    { icon:"voucher", k:t("fx_vouchers"), v:t("fx_vouchers_d"), note:t("fx_vouchers_note") },
  ];
  useEffect(()=>{
    const wrap=document.querySelector(".caps-carousel");
    if(!wrap) return;
    const id=setInterval(()=>wrap.scrollTo({left: ((Math.round(wrap.scrollLeft / wrap.clientWidth)+1)%wrap.children.length)*wrap.clientWidth, behavior:"smooth"}), 3800);
    return ()=>clearInterval(id);
  },[]);
  return (
    <section id="features" className="lx-section caps caps-mobile">
      <div className="lx-container">
        <div className="lx-cxh">
          <h2 className="lx-h2">{t("fx_title")}</h2>
          <p className="lx-copy">{t("fx_sub")}</p>
        </div>
      </div>
      <div className="caps-carousel" data-snap>
        {rows.map((r,i)=>(
          <div key={r.k} className="cap-slide">
            <div className="cap-card">
              <div className="cap-ico big"><Ico type={r.icon}/></div>
              <div className="cap-num top">{String(i+1).padStart(2,"0")}</div>
              <h3 className="cap-title">{r.k}</h3>
              <p className="cap-text">{r.v}</p>
              <div className="cap-note"><span className="cap-note-badge">Note</span><span className="cap-note-text">{r.note}</span></div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function Capabilities({ t }){
  const isMobile=useIsMobile(820);
  const {isAndroid}=usePlatform();
  useEffect(()=>{
    const reduce=window.matchMedia?.("(prefers-reduced-motion: reduce)").matches; if(reduce) return;
    const els=Array.from(document.querySelectorAll("[data-cap]"));
    const io=new IntersectionObserver((entries)=>{ entries.forEach(e=>{ if(e.isIntersecting) e.target.classList.add("show"); else e.target.classList.remove("show"); }); },{threshold:[0.25], rootMargin:"0px 0px -8% 0px"});
    els.forEach(el=>io.observe(el));
    return ()=>io.disconnect();
  },[]);
  if(!isMobile) return <DesktopCapabilities t={t}/>;
  return isAndroid ? <MobileCapabilitiesStack t={t}/> : <MobileCapabilitiesDefault t={t}/>;
}

function Glance({ t }){
  const items=[ {ico:"invoice",title:t("f1"),text:t("f1d")}, {ico:"nfc",title:t("f2"),text:t("f2d")}, {ico:"hive",title:t("f3"),text:t("f3d")} ];
  return (
    <section className="lx-section">
      <div className="lx-container">
        <div className="lx-cxh"><h2 className="lx-h2">{t("overview_title")}</h2></div>
        <div className="lx-cards big equalize">
          {items.map(i=>(
            <article key={i.title} className="card">
              <div className="card-ico"><Ico type={i.ico}/></div>
              <h3 className="card-title">{i.title}</h3>
              <p className="card-text">{i.text}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function Story({ t }){
  return (
    <section className="lx-section story">
      <div className="lx-container">
        <div className="story-head">
          <h2 className="lx-h2">{t("story_title")}</h2>
          <p className="story-lead">{t("story_lead")}</p>
        </div>
        <div className="story-grid">
          <div className="story-box"><div className="story-badge">1</div><h4 className="story-title">{t("f1")}</h4><p className="story-text">{t("f1d")}</p></div>
          <div className="story-box"><div className="story-badge">2</div><h4 className="story-title">{t("f2")}</h4><p className="story-text">{t("f2d")}</p></div>
          <div className="story-box"><div className="story-badge">3</div><h4 className="story-title">{t("f3")}</h4><p className="story-text">{t("f3d")}</p></div>
        </div>
        <div className="story-cols">
          <p className="story-par">{t("story_p1")}</p>
          <p className="story-par">{t("story_p2")}</p>
          <p className="story-par">{t("story_p3")}</p>
        </div>
      </div>
    </section>
  );
}

function MobileMenu({ open, onClose, children }){
  return (
    <div className={`mnav ${open?"open":""}`} onClick={(e)=>{ if(e.target.classList.contains("mnav")) onClose(); }}>
      <div className="mnav-sheet">
        <div className="mnav-head"><strong>Menu</strong><button className="btn" onClick={onClose}>Close</button></div>
        <div className="mnav-body">{children}</div>
      </div>
    </div>
  );
}

export default function Landing(){
  const {lang,setLang,t,dicts}=useI18n();
  const {theme,setTheme}=useTheme();
  useParallax();
  const {isAndroid,isIOS}=usePlatform();
  const logo = theme==="dark"?LogoDark:LogoLight;
  const nfc = useMemo(()=> typeof window!=="undefined" && "NDEFReader" in window,[]);
  const onSeeFeatures=(e)=>{ e.preventDefault(); const el=document.getElementById("features"); if(el) el.scrollIntoView({behavior:"smooth",block:"start"}); };
  const [mOpen,setMOpen]=useState(false);

  return (
    <div className="lx-page">
      <header className="lx-header">
        <div className="lx-container lx-header-inner">
          <a className="lx-brand" href="/" aria-label="HiveDebit">
            <img className="lx-brand-logo" src={logo} alt="HiveDebit" decoding="async" fetchpriority="high" />
          </a>
          <div className="header-controls hide-mobile">
            <a className="btn btn-primary" href="https://hivedebit.com" target="_blank" rel="noreferrer">{t("go_app")}</a>
            <ThemeToggle theme={theme} setTheme={setTheme} t={t} />
            <LangMenu value={lang} onChange={setLang} options={dicts} label={t("language")} />
          </div>
          <button className="nav-toggle show-mobile" aria-label="menu" onClick={()=>setMOpen(true)}><span/><span/><span/></button>
        </div>
      </header>

      <MobileMenu open={mOpen} onClose={()=>setMOpen(false)}>
        <div className="mnav-section">
          <div className="mnav-title">Quick actions</div>
          <a className="btn btn-primary full" href="https://hivedebit.com" target="_blank" rel="noreferrer">{t("go_app")}</a>
        </div>
        <div className="mnav-sep"></div>
        <div className="mnav-section">
          <div className="mnav-title">Preferences</div>
          <div className="mnav-row"><LangMenu value={lang} onChange={(c)=>{setLang(c);}} options={dicts} label={t("language")} compact /></div>
          <div className="mnav-row"><ThemeToggle theme={theme} setTheme={setTheme} t={t} label /></div>
        </div>
        <div className="mnav-sep"></div>
        <div className="mnav-section">
          <div className="mnav-title">Status</div>
          <div className="mnav-row"><span className="badge">{nfc?"NFC ready":"NFC unavailable"}</span></div>
        </div>
      </MobileMenu>

      <div className="lx-bg" aria-hidden>
        <div className="lx-blob a" />
        <div className="lx-blob b" />
        <div className="lx-grid" />
      </div>

      <section className="lx-hero center">
        <div className="lx-container lx-hero-inner" style={{ transform:"translateY(var(--heroY,0)) scale(var(--heroScale,1))" }}>
          <h1 className="lx-title">{t("hero_title")}</h1>
          <p className="lx-sub">{t("hero_sub")}</p>
          <div className="lx-cta">
            <a className="btn btn-primary" href="https://hivedebit.com" target="_blank" rel="noreferrer">{t("enter")}</a>
            <a className="btn" href="#features" onClick={onSeeFeatures}>{t("see_features")}</a>
          </div>
        </div>
      </section>

      <Glance t={t}/>
      <Story t={t}/>
      <section className="lx-section">
        <div className="lx-container"><div className="lx-cxh"><h2 className="lx-h2">{t("wn_title")}</h2></div></div>
        <div className="lx-container">
          <div className="lx-cards big equalize">
            {[{ico:"hive",title:t("wn_1_t"),text:t("wn_1_d")},{ico:"nfc",title:t("wn_2_t"),text:t("wn_2_d")},{ico:"invoice",title:t("wn_3_t"),text:t("wn_3_d")}].map(i=>(
              <article key={i.title} className="card"><div className="card-ico"><Ico type={i.ico}/></div><h3 className="card-title">{i.title}</h3><p className="card-text">{i.text}</p></article>
            ))}
          </div>
        </div>
      </section>
      <Capabilities t={t}/>
      <JeepCluster t={t}/>
      <PostsSection t={t}/>

      <footer className="lx-footer">
        <div className="lx-container lx-footer-inner">
          <span>© {new Date().getFullYear()} HiveDebit — Grand Cherokee</span>
          <span className="lx-badge">{EN.footer_powered}</span>
        </div>
      </footer>
    </div>
  );
}

